import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { HeaderTitleContextProvider } from '../../../contexts/HeaderTitle';
import SandoogLogo from '../../atoms/SandoogLogo';
import Header from '../../organisms/Header';
import SideMenu from '../../molecules/SideMenu';
import WorkspaceSwitcher from '../../organisms/WorkspaceSwitcher';
import {
  StyledCollapseToggle,
  StyledLayoutBaseContent,
  StyledLayoutBaseContentInner,
  StyledLayoutBaseMain,
  StyledLayoutBaseSidebar,
  StyledLayoutBaseWrapper,
  StyledMobileBackdrop,
  StyledSidebarFooter,
  StyledSidebarIdentityArea,
  StyledSidebarLogoRow,
  StyledSwitcherBox,
  StyledUserFooterInfo,
  StyledUserMonogram,
  StyledUserMoreBtn,
} from './styles';
import {
  useClientsDropdownItems,
  useClientSelectValue,
  useOnClientSelectChange,
  useOnLogoutClick,
  useRefetchQueriesOnClientChange,
} from './hooks';
import useUserContext from '../../../hooks/useUserContext';
import { LayoutBaseProps } from './types';
import usePathMatchesRoute from '../../../hooks/usePathMatchesRoute';
import routes from '../../../setup/consts/routes';
import { useAuthTokens } from '../../../contexts/User/hooks';
import IconButtonLink from '../../atoms/IconButton/iconButtonLink';
import {
  IconChevronLeft,
  IconClose,
  IconStats,
  IconWarehouse2,
} from '../../../assets/svg/icons';
import { bp } from '../../../theme/mediaQueries';

const COLLAPSE_KEY = 'sdg.sidebar.collapsed';

const IconMenu = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={2}
    strokeLinecap='round'
  >
    <line x1='3' y1='6' x2='21' y2='6' />
    <line x1='3' y1='12' x2='21' y2='12' />
    <line x1='3' y1='18' x2='21' y2='18' />
  </svg>
);

const MoreDotsIcon = () => (
  <svg viewBox='0 0 20 20' fill='currentColor' width={16} height={16}>
    <circle cx='4' cy='10' r='1.5' />
    <circle cx='10' cy='10' r='1.5' />
    <circle cx='16' cy='10' r='1.5' />
  </svg>
);

const getMonogramInitials = (name: string): string => {
  const words = name
    .replace(/[|،]/g, ' ')
    .split(/\s+/)
    .filter((w) => /[a-zA-Z؀-ۿ]/.test(w));
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

const LayoutBase: React.FC<LayoutBaseProps> = ({
  children,
  items,
  settingsItems,
  ...props
}) => {
  const intl = useIntl();
  const history = useHistory();
  const onLogout = useOnLogoutClick();
  const { user } = useUserContext();
  const {
    items: clientSelectItems,
    fetchMore,
    hasNextPage,
  } = useClientsDropdownItems();
  const onClientSelectChange = useOnClientSelectChange();
  const value = useClientSelectValue();
  const isSettingsSection = usePathMatchesRoute(
    routes.SETTINGS_SECTION,
    undefined,
    false
  );
  const isWarehousePage = usePathMatchesRoute(routes.WAREHOUSE, undefined);
  const isReportPage = usePathMatchesRoute(routes.REPORT, undefined);
  const authTokens = useAuthTokens();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  });

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    }
  }, [collapsed]);

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${bp.md}px)`);
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setMobileOpen(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault();
        setCollapsed((c) => !c);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [children]);

  useRefetchQueriesOnClientChange(value?.value, value?.checkedSubItems);

  const selectedClient = useMemo(
    () =>
      clientSelectItems.find(
        ({ value: clientId }) => clientId === value?.value
      ),
    [value?.value, clientSelectItems]
  );

  const workspaceName = useMemo(() => {
    if (typeof selectedClient?.label !== 'string') return undefined;
    const label = selectedClient.label;
    const pipeIdx = label.indexOf(' | ');
    return pipeIdx !== -1 ? label.slice(0, pipeIdx) : label;
  }, [selectedClient]);

  const userInitials = useMemo(
    () => (user ? getMonogramInitials(user.name) : undefined),
    [user]
  );

  const userRole = useMemo(() => {
    if (!user) return undefined;
    if (user.isSandoogUser) return 'Sandoog';
    if (user.roles?.length) return user.roles[0];
    return undefined;
  }, [user]);

  const SwitcherComponent = (isReportPage || isWarehousePage) && (
    <StyledSwitcherBox>
      <IconButtonLink
        isActive={isWarehousePage}
        isSecondary
        icon={IconWarehouse2}
        to={routes.WAREHOUSE}
      />
      <IconButtonLink
        isActive={isReportPage}
        isSecondary
        icon={IconStats}
        to={routes.REPORT}
      />
    </StyledSwitcherBox>
  );

  return (
    <HeaderTitleContextProvider>
      <StyledLayoutBaseWrapper>
        <StyledMobileBackdrop
          visible={mobileOpen}
          onClick={() => setMobileOpen(false)}
          aria-hidden='true'
        />

        <StyledLayoutBaseSidebar collapsed={collapsed} mobileOpen={mobileOpen}>
          {/* Logo row — toggle lives here when expanded, below when collapsed */}
          <StyledSidebarLogoRow collapsed={collapsed}>
            {collapsed ? (
              <SandoogLogo size={32} light />
            ) : (
              <>
                <SandoogLogo size={32} withText light />
                <StyledCollapseToggle
                  type='button'
                  onClick={() => setCollapsed((c) => !c)}
                  aria-label='Collapse sidebar'
                  title='Collapse sidebar (⌘\)'
                >
                  <IconChevronLeft />
                </StyledCollapseToggle>
              </>
            )}
          </StyledSidebarLogoRow>

          {/* Collapsed toggle — centered below logo row */}
          {collapsed && (
            <StyledCollapseToggle
              type='button'
              collapsed
              onClick={() => setCollapsed((c) => !c)}
              aria-label='Expand sidebar'
              title='Expand sidebar (⌘\)'
            >
              <IconChevronLeft style={{ transform: 'rotate(180deg)' }} />
            </StyledCollapseToggle>
          )}

          {/* Workspace switcher */}
          {user && (
            <StyledSidebarIdentityArea collapsed={collapsed}>
              <WorkspaceSwitcher
                items={clientSelectItems}
                value={value}
                onChange={onClientSelectChange}
                collapsed={collapsed}
                userLogo={user.accountDetails.logo ?? undefined}
                userName={user.name}
                storeName={workspaceName}
                disabled={isSettingsSection && !authTokens?.sdgAuthToken}
                onDisabledClick={() => history.push(routes.ORDERS)}
              />
            </StyledSidebarIdentityArea>
          )}

          {/* Primary nav */}
          <SideMenu {...{ items, ...props }} collapsed={collapsed} />

          {/* User footer */}
          <StyledSidebarFooter collapsed={collapsed}>
            <StyledUserMonogram>
              {userInitials ?? '?'}
            </StyledUserMonogram>
            {!collapsed && user && (
              <>
                <StyledUserFooterInfo>
                  <div className='name'>{user.name}</div>
                  {userRole && <div className='role'>{userRole}</div>}
                </StyledUserFooterInfo>
                <StyledUserMoreBtn
                  type='button'
                  aria-label='More options'
                  onClick={() => history.push(routes.SETTINGS_SECTION)}
                >
                  <MoreDotsIcon />
                </StyledUserMoreBtn>
              </>
            )}
          </StyledSidebarFooter>
        </StyledLayoutBaseSidebar>

        <StyledLayoutBaseMain>
          <Header
            menu={{
              items: settingsItems,
              title: intl.formatMessage({
                id: 'sidebar.settings.title',
                defaultMessage: 'Settings',
              }),
              onLogout,
            }}
            centerComponent={SwitcherComponent}
            onMenuToggle={() => setMobileOpen((o) => !o)}
            mobileMenuOpen={mobileOpen}
            workspaceName={workspaceName}
            userInitials={userInitials}
          />
          <StyledLayoutBaseContent id='main-content'>
            <StyledLayoutBaseContentInner>{children}</StyledLayoutBaseContentInner>
          </StyledLayoutBaseContent>
        </StyledLayoutBaseMain>
      </StyledLayoutBaseWrapper>
    </HeaderTitleContextProvider>
  );
};

export default LayoutBase;
